// Phase 4-D6 GREEN — DY9nl 举报 modal
//
// frame_id=DY9nl @ ui/dogdisk-user.pen modals.md §DY9nl(2026-05-21 补画 #6)
// 触发:`OhKgp moreBtn (QCv8l)` → 弹 BottomSheet
//
// 实现决策(per spec §7):
//   - 4 reason radio(spec §4.5:侵权 / 盗版 / 诈骗 / 其他)+ textarea ≤500 字
//   - 复用 POST /gapi/user/tickets(type='other' + description 头部结构化
//     标记 `[举报 target_user_id=<uuid>; reason_key=<key>]`,客服端可 regex 匹配)
//   - 与既有 report-dialog.tsx 边界:report-dialog 6 reason + 200 字 旧版;
//     本 DY9nl 4 reason + 500 字 新版(D6 frame spec);后续可统一,本批次保留并存
//
// 注:服务端 CreateTicketInput type 当前 enum = account/payment/file/share/report/other
// (D33 加 'report');本 modal 沿用 'other' 不触 D33 'report' 强约束(target_user_id 必填),
// 保留前端结构化 description 头部解析方案。
//
// data-testid 约定:
//   - 根:DY9nl
//   - reason radio:DY9nl-reason-{copyright,piracy,fraud,other}
//   - textarea:DY9nl-textarea
//   - submit:DY9nl-submit
//   - cancel:DY9nl-cancel

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiClient, ApiClientError, randomUUID } from '@dogdisk/shared'

const REASONS: { key: 'copyright' | 'piracy' | 'fraud' | 'other'; label: string }[] = [
  { key: 'copyright', label: '侵权(他人作品 / 商标)' },
  { key: 'piracy', label: '盗版资源 / 破解软件' },
  { key: 'fraud', label: '诈骗 / 钓鱼 / 恶意软件' },
  { key: 'other', label: '其他(请在下方说明)' },
]

export type ReportModalProps = {
  open: boolean
  onClose: () => void
  targetUserId: string
  targetUsername?: string | undefined
  onSubmitted?: () => void
}

export function ReportModal({
  open,
  onClose,
  targetUserId,
  targetUsername,
  onSubmitted,
}: ReportModalProps) {
  const [reasonKey, setReasonKey] = useState<typeof REASONS[number]['key']>('fraud')
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: async () => {
      const reasonLabel = REASONS.find((r) => r.key === reasonKey)?.label ?? reasonKey
      const description = [
        `[举报 target_user_id=${targetUserId}; reason_key=${reasonKey}]`,
        `举报对象:${targetUsername ?? targetUserId}`,
        `原因:${reasonLabel}`,
        note.trim() ? `补充:${note.trim()}` : '',
      ]
        .filter(Boolean)
        .join('\n')

      return apiClient.post('/gapi/user/tickets', {
        title: `举报用户 ${targetUsername ?? targetUserId.slice(0, 8)}`,
        type: 'other',
        description,
        attachments: [],
        idempotency_key: randomUUID(),
      })
    },
    onSuccess: () => {
      setSubmitted(true)
      onSubmitted?.()
    },
    onError: (e: Error) => {
      if (e instanceof ApiClientError) {
        setErr(e.message || '举报提交失败')
      } else {
        setErr(e.message || '举报提交失败')
      }
    },
  })

  if (!open) return null

  return (
    <div
      data-frame-id="DY9nl"
      data-testid="DY9nl"
      role="dialog"
      aria-modal="true"
      aria-label="举报"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-3">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 px-6 py-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ebfaf6] text-[#19caad]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M5 12l5 5 9-12" />
              </svg>
            </span>
            <p className="text-[15px] font-semibold text-[#1A1A1A]">举报已提交</p>
            <p className="text-center text-[12px] text-[#9CA3AF]">
              我们会尽快核实处理。处理结果可在「我的工单」查看。
            </p>
            <button
              type="button"
              data-testid="DY9nl-close"
              onClick={onClose}
              className="mt-2 h-11 w-full rounded-lg bg-[#19caad] text-[14px] font-semibold text-white"
            >
              我知道了
            </button>
          </div>
        ) : (
          <>
            <header className="px-6 py-3 text-center">
              <h2 className="text-[15px] font-semibold text-[#1A1A1A]">举报这个用户/资源</h2>
              <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
                提交后自动创建一条「举报」类型工单,客服会处理。匿名提交。
              </p>
            </header>

            <fieldset className="flex flex-col gap-1 px-6 pb-3">
              <legend className="pb-2 text-[12px] text-[#9CA3AF]">选择举报原因</legend>
              {REASONS.map((r) => (
                <label
                  key={r.key}
                  data-testid={`DY9nl-reason-${r.key}`}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] text-[#1A1A1A] active:bg-[#F6F7F8]"
                >
                  <input
                    type="radio"
                    name="dy9nl-reason"
                    value={r.key}
                    checked={reasonKey === r.key}
                    onChange={() => setReasonKey(r.key)}
                    className="h-4 w-4 accent-[#19caad]"
                  />
                  {r.label}
                </label>
              ))}
            </fieldset>

            <div className="px-6 pb-3">
              <label className="block pb-1 text-[12px] text-[#9CA3AF]" htmlFor="dy9nl-textarea">
                具体说明(≤500 字 · {note.length}/500)
              </label>
              <textarea
                id="dy9nl-textarea"
                data-testid="DY9nl-textarea"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="请尽量具体描述,帮我们更快处理"
                className="w-full resize-none rounded-lg bg-[#F6F7F8] p-3 text-[13px] outline-none placeholder:text-[#9CA3AF]"
              />
            </div>

            {err && <p className="px-6 pb-2 text-[12px] text-[#FF6B6B]">{err}</p>}

            <div className="flex gap-2 px-6 pb-3">
              <button
                type="button"
                data-testid="DY9nl-cancel"
                onClick={onClose}
                className="h-11 flex-1 rounded-lg bg-[#F1F2F4] text-[14px] font-semibold text-[#1A1A1A]"
              >
                取消
              </button>
              <button
                type="button"
                data-testid="DY9nl-submit"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
                className="h-11 flex-1 rounded-lg bg-[#FF6B6B] text-[14px] font-semibold text-white disabled:opacity-50"
              >
                {mutation.isPending ? '提交中…' : '提交举报'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
